<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useClientStore } from '@/stores/client'
import { type DriverConfig, driverConfigsApi } from '@/api/entities/review/driverConfigs'
import { uploadReportApi } from '@/api/entities/review/uploadReport'

definePage({
  meta: {
    title: '업로드 보고서',
    action: 'read',
    subject: 'review',
  },
})

const MSG_VREVIEW_RESULT_CHECK_FAILED = '브이리뷰 이관 결과 확인에 실패했습니다.'

const { snackbar, handleError } = useErrorHandler()

const clientStore = useClientStore()
const selectedClient = computed(() => clientStore.selectedClient)

// 상태
const loading = ref(false)
const startDate = ref<string>('')
const endDate = ref<string>('')
const driverConfigs = ref<DriverConfig[]>([])
const selectedChannel = ref<DriverConfig | null>(null)

// 보고서 데이터
const clientTotalReport = ref<any[]>([])
const channelReport = ref<any[]>([])
const itemReport = ref<any[]>([])

// 실패 로그 모달
const failedLogDialog = ref(false)
const failedLogs = ref<any[]>([])
const checkResultLoading = ref(false)

// 테이블 헤더
const totalReportHeaders = [
  { title: '총 리뷰', key: 'total', sortable: true },
  { title: '대기', key: 'ready_count', sortable: true },
  { title: '완료', key: 'finished_count', sortable: true },
  { title: '실패', key: 'failed_count', sortable: true },
  { title: '보류', key: 'pending_count', sortable: true },
]

const channelReportHeaders = [
  { title: '채널', key: 'channel_name', sortable: true },
  { title: '대기', key: 'ready_count', sortable: true },
  { title: '완료', key: 'finished_count', sortable: true },
  { title: '실패', key: 'failed_count', sortable: true },
  { title: '보류', key: 'pending_count', sortable: true },
]

const itemReportHeaders = [
  { title: '상품코드', key: 'item_code', sortable: true },
  { title: '상품명', key: 'item_name', sortable: true },
  { title: '총 리뷰', key: 'total', sortable: true },
  { title: '대기', key: 'ready_count', sortable: true },
  { title: '완료', key: 'finished_count', sortable: true },
  { title: '실패', key: 'failed_count', sortable: true },
  { title: '보류', key: 'pending_count', sortable: true },
  { title: '', key: 'action', sortable: false },
]

// 숫자 포맷
const formatNumber = (num: number | null | undefined) => {
  if (num === null || num === undefined)
    return '0'

  return new Intl.NumberFormat('ko-KR').format(num)
}

// 데이터 로드
const loadDriverConfigs = async () => {
  if (!selectedClient.value)
    return

  try {
    const { success, data } = await driverConfigsApi.listDriverConfigs(selectedClient.value.id, {
      per_page: 100,
    })

    if (success && data)
      driverConfigs.value = data.data || []
  }
  catch (error) {
    handleError(error, { notificationMessage: '드라이버 설정을 불러오는데 실패했습니다.' })
  }
}

const loadTotalReport = async () => {
  if (!selectedClient.value)
    return

  loading.value = true
  try {
    const params: Record<string, any> = {}
    if (startDate.value && endDate.value) {
      params.start_date = startDate.value
      params.end_date = endDate.value
    }

    const { success, data } = await uploadReportApi.getClientTotalReport(selectedClient.value.id, params)

    if (success && data)
      clientTotalReport.value = Array.isArray(data) ? data : [data]
  }
  catch (error) {
    handleError(error, { notificationMessage: '전체 보고서를 불러오는데 실패했습니다.' })
  }
  finally {
    loading.value = false
  }
}

const loadChannelReport = async () => {
  if (!selectedClient.value)
    return

  try {
    const params: Record<string, any> = {}
    if (startDate.value && endDate.value) {
      params.start_date = startDate.value
      params.end_date = endDate.value
    }

    const { success, data } = await uploadReportApi.getChannelReport(selectedClient.value.id, params)

    if (success && data)
      channelReport.value = data || []
  }
  catch (error) {
    handleError(error, { notificationMessage: '채널별 보고서를 불러오는데 실패했습니다.' })
  }
}

const loadItemReport = async () => {
  if (!selectedClient.value || !selectedChannel.value)
    return

  try {
    const params: Record<string, any> = {
      driver_config_id: selectedChannel.value.id,
    }

    if (startDate.value && endDate.value) {
      params.start_date = startDate.value
      params.end_date = endDate.value
    }

    const { success, data } = await uploadReportApi.getItemReport(selectedClient.value.id, params)

    if (success && data)
      itemReport.value = data || []
  }
  catch (error) {
    handleError(error, { notificationMessage: '상품별 보고서를 불러오는데 실패했습니다.' })
  }
}

const refresh = () => {
  loadTotalReport()
  loadChannelReport()
  if (selectedChannel.value)
    loadItemReport()
}

const showFailedLogModal = async (itemCode: string) => {
  if (!selectedClient.value || !selectedChannel.value)
    return

  try {
    const { success, data } = await uploadReportApi.getFailedLogs(
      selectedClient.value.id,
      selectedChannel.value.id,
      itemCode,
    )

    if (success && data) {
      failedLogs.value = data
      failedLogDialog.value = true
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '실패 로그를 불러오는데 실패했습니다.' })
  }
}

const checkResult = async () => {
  if (!selectedClient.value)
    return

  if (checkResultLoading.value)
    return

  checkResultLoading.value = true
  try {
    const { success, data, error } = await uploadReportApi.checkVReviewResult(selectedClient.value.id)

    if (success) {
      const pendingCount = (data as any)?.pending_count

      const pendingText = pendingCount !== null && pendingCount !== undefined
        ? ` (대기: ${formatNumber(pendingCount)}건)`
        : ''

      snackbar.success(`브이리뷰 이관 결과 확인 요청 완료${pendingText}`)
      refresh()
    }
    else if (error) {
      handleError(error, { notificationMessage: MSG_VREVIEW_RESULT_CHECK_FAILED })
    }
    else {
      snackbar.error(MSG_VREVIEW_RESULT_CHECK_FAILED)
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: MSG_VREVIEW_RESULT_CHECK_FAILED })
  }
  finally {
    checkResultLoading.value = false
  }
}

// 감시자
watch(selectedClient, () => {
  if (selectedClient.value) {
    loadDriverConfigs()
    loadTotalReport()
    loadChannelReport()
  }
  else {
    driverConfigs.value = []
    clientTotalReport.value = []
    channelReport.value = []
    itemReport.value = []
  }
})

watch(selectedChannel, () => {
  if (selectedChannel.value)
    loadItemReport()
  else
    itemReport.value = []
})

watch([startDate, endDate], () => {
  if (startDate.value && endDate.value)
    refresh()
})

onMounted(() => {
  if (selectedClient.value) {
    loadDriverConfigs()
    loadTotalReport()
    loadChannelReport()
  }
})
</script>

<template>
  <div>
    <!-- 상단 필터 섹션 -->
    <VRow class="mb-4">
      <VCol
        cols="12"
        md="4"
      >
        <ClientSelector />
      </VCol>
      <VCol
        cols="12"
        md="4"
      >
        <VCard>
          <VCardText>
            <h6 class="text-h6 mb-2">
              기간
            </h6>
            <VRow>
              <VCol cols="6">
                <VTextField
                  v-model="startDate"
                  type="date"
                  label="시작일"
                  density="compact"
                  hide-details
                />
              </VCol>
              <VCol cols="6">
                <VTextField
                  v-model="endDate"
                  type="date"
                  label="종료일"
                  density="compact"
                  hide-details
                />
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
      <VCol
        cols="12"
        md="4"
      >
        <VCard>
          <VCardText>
            <VBtn
              v-if="selectedClient && selectedClient.review_service === 'v_review'"
              color="primary"
              :loading="checkResultLoading"
              :disabled="checkResultLoading"
              @click="checkResult"
            >
              브이리뷰 이관 결과 확인 요청
            </VBtn>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 보고서 섹션 -->
    <VRow class="mb-4">
      <!-- 전체 업로드 결과 -->
      <VCol
        cols="12"
        md="6"
      >
        <VCard v-if="selectedClient">
          <VCardTitle class="d-flex justify-space-between align-center">
            전체 업로드 결과
            <VBtn
              icon="tabler-refresh"
              variant="text"
              size="small"
              @click="loadTotalReport"
            />
          </VCardTitle>
          <VCardText>
            <VDataTable
              :headers="totalReportHeaders"
              :items="clientTotalReport"
              :loading="loading"
              hide-default-footer
              class="elevation-0"
            >
              <template #item.total="{ item }">
                {{ formatNumber((item.ready_count || 0) + (item.finished_count || 0) + (item.failed_count || 0) + (item.pending_count || 0)) }}
              </template>
              <template #item.ready_count="{ item }">
                {{ formatNumber(item.ready_count) }}
              </template>
              <template #item.finished_count="{ item }">
                {{ formatNumber(item.finished_count) }}
              </template>
              <template #item.failed_count="{ item }">
                {{ formatNumber(item.failed_count) }}
              </template>
              <template #item.pending_count="{ item }">
                {{ formatNumber(item.pending_count) }}
              </template>
            </VDataTable>
          </VCardText>
        </VCard>
      </VCol>

      <!-- 채널별 업로드 결과 -->
      <VCol
        cols="12"
        md="6"
      >
        <VCard v-if="selectedClient">
          <VCardTitle class="d-flex justify-space-between align-center">
            채널별 업로드 결과
            <VBtn
              icon="tabler-refresh"
              variant="text"
              size="small"
              @click="loadChannelReport"
            />
          </VCardTitle>
          <VCardText>
            <VDataTable
              :headers="channelReportHeaders"
              :items="channelReport"
              :loading="loading"
              class="elevation-0"
            >
              <template #item.ready_count="{ item }">
                {{ formatNumber(item.ready_count) }}
              </template>
              <template #item.finished_count="{ item }">
                {{ formatNumber(item.finished_count) }}
              </template>
              <template #item.failed_count="{ item }">
                {{ formatNumber(item.failed_count) }}
              </template>
              <template #item.pending_count="{ item }">
                {{ formatNumber(item.pending_count) }}
              </template>
            </VDataTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 상품별 업로드 결과 -->
    <VRow>
      <VCol cols="12">
        <VCard v-if="selectedClient">
          <VCardTitle class="d-flex justify-space-between align-center">
            상품별 업로드 결과
            <VBtn
              icon="tabler-refresh"
              variant="text"
              size="small"
              @click="loadItemReport"
            />
          </VCardTitle>
          <VCardText>
            <VRow class="mb-4">
              <VCol
                cols="12"
                md="4"
              >
                <VSelect
                  v-model="selectedChannel"
                  :items="driverConfigs"
                  :item-title="(item: DriverConfig) => item.download_driver?.name || `드라이버 ${item.id}`"
                  return-object
                  label="채널 선택"
                  density="compact"
                  clearable
                  hide-details
                />
              </VCol>
            </VRow>

            <VDataTable
              :headers="itemReportHeaders"
              :items="itemReport"
              :loading="loading"
              class="elevation-0"
            >
              <template #item.total="{ item }">
                {{ formatNumber((item.ready_count || 0) + (item.finished_count || 0) + (item.failed_count || 0) + (item.pending_count || 0)) }}
              </template>
              <template #item.ready_count="{ item }">
                {{ formatNumber(item.ready_count) }}
              </template>
              <template #item.finished_count="{ item }">
                {{ formatNumber(item.finished_count) }}
              </template>
              <template #item.failed_count="{ item }">
                {{ formatNumber(item.failed_count) }}
              </template>
              <template #item.pending_count="{ item }">
                {{ formatNumber(item.pending_count) }}
              </template>
              <template #item.action="{ item }">
                <VBtn
                  v-if="item.failed_count > 0"
                  color="warning"
                  size="small"
                  @click="showFailedLogModal(item.item_code)"
                >
                  실패 로그
                </VBtn>
              </template>
            </VDataTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 실패 로그 모달 -->
    <VDialog
      v-model="failedLogDialog"
      max-width="1200"
    >
      <VCard>
        <VCardTitle>실패 로그</VCardTitle>
        <VCardText>
          <VDataTable
            :items="failedLogs"
            class="elevation-0"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="failedLogDialog = false"
          >
            닫기
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar.isVisible.value"
      :color="snackbar.color.value"
      :timeout="snackbar.timeout.value"
    >
      {{ snackbar.message.value }}
    </VSnackbar>
  </div>
</template>
